import { Select } from "antd";
import { useEffect, useState } from "react";
import { AddItemsPickerButton } from "../../../../shared/orderLineForm/components/addItemsPickerStyles";
import { FulfilLineGrid, productLabel } from "../FulfilLineGrid";
import {
  SectionHint,
  SectionTitle,
  SelectionPill,
  StepFooter,
  StepOptions,
  StepResult,
  StepState,
} from "../FulfilParts";
import { totalQty, trimQty } from "../fulfilHelpers";
import { useFulfilStep } from "../useFulfilStep";
import { useStepSelection } from "../useStepSelection";

const remaining = row => row.remaining_qty;

const COLUMNS = [
  { key: "line", label: "#", render: row => row.line_number },
  { key: "product", label: "Product", render: row => productLabel(row.product) },
  { key: "picked", label: "Picked", numeric: true, render: row => trimQty(row.qty_picked) },
  { key: "packed", label: "Packed", numeric: true, render: row => trimQty(row.packed_qty) },
  { key: "left", label: "Left to pack", numeric: true, render: row => trimQty(row.remaining_qty) },
];

/**
 * Step 3: put picked goods into a pack (carton, pallet…).
 *
 * @param {{orderId: (string|number), onNext: function()}} props
 */
export const PackStep = ({ orderId, onNext }) => {
  const { query, rows, defaults, run, saving, result, clearResult } = useFulfilStep(
    orderId,
    "pack",
  );
  const selection = useStepSelection(rows, {
    idKey: "picklist_line_id",
    defaultQty: remaining,
    maxQty: remaining,
  });
  const [packagingTypeId, setPackagingTypeId] = useState(null);
  const [packDate, setPackDate] = useState("");
  const [confirmAndRfd, setConfirmAndRfd] = useState(true);

  useEffect(() => {
    setPackagingTypeId(defaults.default_packaging_type_id ?? null);
    setPackDate(defaults.pack_date || "");
    if (defaults.confirm_and_rfd !== undefined) setConfirmAndRfd(Boolean(defaults.confirm_and_rfd));
  }, [defaults]);

  if (result) {
    return (
      <StepResult result={result} nextLabel="Next: Ship" onNext={onNext} onAgain={clearResult} />
    );
  }

  const count = selection.selectedRows.length;
  const total = trimQty(totalQty(selection.checked, selection.quantities));

  const submit = () =>
    run({
      packaging_type_id: packagingTypeId,
      ...(packDate ? { pack_date: packDate } : {}),
      lines: selection.selectedRows.map(row => ({
        picklist_line_id: row.picklist_line_id,
        pack_qty: selection.quantities[row.picklist_line_id],
      })),
      confirm_and_rfd: confirmAndRfd,
    });

  return (
    <>
      <SectionTitle>Pack</SectionTitle>
      <SectionHint>
        Choose what's going in the pack. Lines for different warehouses or delivery addresses go
        into separate packs automatically.
      </SectionHint>
      <StepState query={query} emptyText="Nothing picked is waiting to be packed.">
        <FulfilLineGrid
          rows={rows}
          idKey="picklist_line_id"
          columns={COLUMNS}
          selection={selection}
          qtyLabel="Pack now"
          disabled={saving}
          ariaLabel="Lines to pack"
        />
        <StepOptions>
          <label>
            Packaging
            <Select
              size="small"
              style={{ minWidth: 200 }}
              placeholder="Choose packaging"
              value={packagingTypeId ?? undefined}
              onChange={setPackagingTypeId}
              status={packagingTypeId ? undefined : "error"}
              options={(defaults.packaging_types || []).map(type => ({
                value: type.id,
                label: type.name,
              }))}
              aria-label="Packaging"
            />
          </label>
          <label>
            Pack date
            <input
              type="date"
              value={packDate}
              onChange={event => setPackDate(event.target.value)}
            />
          </label>
          <label className="inline">
            <input
              type="checkbox"
              checked={confirmAndRfd}
              onChange={event => setConfirmAndRfd(event.target.checked)}
            />
            Confirm the pack and mark it ready for dispatch
          </label>
        </StepOptions>
        <StepFooter pill={<SelectionPill selected={count} total={rows.length} units={total} />}>
          <AddItemsPickerButton
            type="primary"
            loading={saving}
            disabled={!count || selection.hasErrors || !packagingTypeId || saving}
            onClick={submit}
          >
            Create pack
          </AddItemsPickerButton>
        </StepFooter>
      </StepState>
    </>
  );
};
